/**
 * Configuration (sync settings). Backend contract is the source of truth:
 *   GET/PATCH /api/v1/admin/settings/sync
 *   schema: app/schemas/sync_settings.py (SyncSettingsOut / SyncSettingsPatch)
 *
 * Layout:
 *   1. Scheduled sync (enable + next-run on one row)
 *   2. Schedule (frequency, time IST, weekday/month day)
 *   3. Operational settings (windows / overlaps)
 *   4. System values (timezone, next run, page sizes, chain state - display only)
 *
 * `apiDefaultPageSize` / `apiMaxPageSize` are GET-only (env-sourced) - never
 * sent on PATCH.
 */
import { el, mount, type ElChild } from "../utils/dom";
import { renderPageHeader } from "../components/pageHeader";
import {
  renderLoadingState,
  renderErrorState,
  renderUnavailableState,
  renderEmptyState,
} from "../components/states";
import { renderStatusText, runStatusTone } from "../components/statusText";
import { renderAbsoluteTime } from "../components/dateTime";
import { absoluteTime, EMPTY, relativeTime } from "../utils/format";
import { getSyncSettings, patchSyncSettings } from "../services/admin";
import { ApiError } from "../services/apiClient";
import { showToast } from "../components/toast";
import { confirmAction } from "../components/confirmDialog";
import { HELP, labelWithHelp } from "../components/tooltip";
import type { PatchSyncSettingsBody } from "../services/admin";
import type { ScheduleKind, SyncSettings } from "../types/api";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface FormState {
  syncEnabled: boolean;
  scheduleKind: ScheduleKind;
  scheduleTime: string;
  weekdays: number[];
  monthDay: number;
  timesheetSyncWindowDays: number;
  timesheetSyncOverlapDays: number;
  timesheetSyncLookbackDays: number;
  lastModifiedOverlapHours: number;
}

function toFormState(s: SyncSettings): FormState {
  return {
    syncEnabled: s.syncEnabled,
    scheduleKind: s.scheduleKind,
    scheduleTime: s.scheduleTime,
    weekdays: [...(s.weekdays ?? [])],
    monthDay: s.monthDay ?? 1,
    timesheetSyncWindowDays: s.timesheetSyncWindowDays,
    timesheetSyncOverlapDays: s.timesheetSyncOverlapDays,
    timesheetSyncLookbackDays: s.timesheetSyncLookbackDays,
    lastModifiedOverlapHours: s.lastModifiedOverlapHours,
  };
}

function sameWeekdays(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const as = [...a].sort();
  const bs = [...b].sort();
  return as.every((v, i) => v === bs[i]);
}

function isDirty(form: FormState, latest: SyncSettings): boolean {
  const baseline = toFormState(latest);
  return (
    form.syncEnabled !== baseline.syncEnabled ||
    form.scheduleKind !== baseline.scheduleKind ||
    form.scheduleTime !== baseline.scheduleTime ||
    form.monthDay !== baseline.monthDay ||
    form.timesheetSyncWindowDays !== baseline.timesheetSyncWindowDays ||
    form.timesheetSyncOverlapDays !== baseline.timesheetSyncOverlapDays ||
    form.timesheetSyncLookbackDays !== baseline.timesheetSyncLookbackDays ||
    form.lastModifiedOverlapHours !== baseline.lastModifiedOverlapHours ||
    !sameWeekdays(form.weekdays, baseline.weekdays)
  );
}

export class ConfigurationPage {
  private body: HTMLElement = el("div", {}, [renderLoadingState("Loading configuration…")]);
  private latest: SyncSettings | null = null;
  private form: FormState | null = null;
  private saving = false;
  private onBeforeUnload: ((ev: BeforeUnloadEvent) => void) | null = null;

  mount(outlet: HTMLElement, navigate: (path: string) => void): void {
    const refreshButton = el(
      "button",
      {
        className: "btn btn-ghost btn-sm",
        type: "button",
        "aria-label": "Refresh configuration",
        onclick: () => void this.refresh(),
      },
      ["Refresh"]
    );

    mount(
      outlet,
      el("div", {}, [
        renderPageHeader({
          title: "Configuration",
          subtitle: "When automatic sync runs, and how each run selects data.",
          back: { fallbackPath: "/dashboard", navigate, preferHistory: false, label: "Back to Dashboard" },
          actions: [refreshButton],
        }),
        this.body,
      ])
    );

    this.onBeforeUnload = (ev: BeforeUnloadEvent) => {
      if (this.latest && this.form && isDirty(this.form, this.latest)) {
        ev.preventDefault();
        ev.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", this.onBeforeUnload);

    void this.load();
  }

  destroy(): void {
    if (this.onBeforeUnload) {
      window.removeEventListener("beforeunload", this.onBeforeUnload);
      this.onBeforeUnload = null;
    }
  }

  private async refresh(): Promise<void> {
    if (this.latest && this.form && isDirty(this.form, this.latest)) {
      const ok = await confirmAction({
        title: "Discard unsaved changes?",
        message: "Refreshing reloads settings from the server and discards edits on this page.",
        confirmLabel: "Discard and refresh",
        danger: true,
      });
      if (!ok) return;
    }
    await this.load();
  }

  private async load(): Promise<void> {
    mount(this.body, renderLoadingState("Loading configuration…"));
    try {
      this.latest = await getSyncSettings();
      this.form = toFormState(this.latest);
      this.render();
    } catch (err) {
      this.latest = null;
      this.form = null;
      if (err instanceof ApiError && (err.status === 401 || err.status === 503)) {
        mount(this.body, renderUnavailableState(err.message));
      } else {
        mount(this.body, renderErrorState(errorMessage(err), { onRetry: () => void this.load() }));
      }
    }
  }

  private render(): void {
    if (!this.latest || !this.form) {
      mount(this.body, renderEmptyState("No configuration is available."));
      return;
    }

    mount(
      this.body,
      el("div", {}, [
        this.renderEnablement(),
        this.renderSchedule(),
        this.renderOperational(),
        renderReadOnlySystem(this.latest),
        this.renderFooter(),
      ])
    );
  }

  private touch(): void {
    this.render();
  }

  /**
   * Lightweight update for changes that don't affect form layout (number
   * inputs, time input). Re-renders only the footer in-place so the dirty
   * indicator and Save button stay current without unmounting the whole form,
   * preserving input focus and cursor position.
   */
  private touchLight(): void {
    if (!this.latest || !this.form) return;
    const footer = this.body.querySelector<HTMLElement>(".config-footer");
    if (!footer) {
      // Footer not yet in DOM (first render still pending) - full render.
      this.render();
      return;
    }
    footer.replaceWith(this.renderFooter());
  }

  private discard(): void {
    if (!this.latest) return;
    this.form = toFormState(this.latest);
    this.render();
    showToast("Unsaved changes cancelled.", "info");
  }

  private async save(): Promise<void> {
    if (!this.form || !this.latest || this.saving) return;

    if (this.form.scheduleKind === "weekly" && this.form.weekdays.length === 0) {
      showToast("Select at least one weekday for a weekly schedule.", "error");
      return;
    }

    if (!isDirty(this.form, this.latest)) {
      showToast("No changes to save.", "info");
      return;
    }

    const confirmed = await confirmAction({
      title: "Save configuration?",
      message: saveSummary(this.form, this.latest),
      confirmLabel: "Save",
    });
    if (!confirmed) return;

    this.saving = true;
    this.render();

    const body: PatchSyncSettingsBody = {
      syncEnabled: this.form.syncEnabled,
      scheduleKind: this.form.scheduleKind,
      scheduleTime: this.form.scheduleTime,
      weekdays: this.form.scheduleKind === "weekly" ? this.form.weekdays : null,
      monthDay: this.form.scheduleKind === "monthly" ? this.form.monthDay : null,
      timesheetSyncWindowDays: this.form.timesheetSyncWindowDays,
      timesheetSyncOverlapDays: this.form.timesheetSyncOverlapDays,
      timesheetSyncLookbackDays: this.form.timesheetSyncLookbackDays,
      lastModifiedOverlapHours: this.form.lastModifiedOverlapHours,
    };

    try {
      this.latest = await patchSyncSettings(body);
      this.form = toFormState(this.latest);
      showToast("Configuration saved.", "success");
    } catch (err) {
      showToast(errorMessage(err), "error");
    } finally {
      this.saving = false;
      this.render();
    }
  }

  /* ---------------- 1. Sync enablement ---------------- */

  private renderEnablement(): HTMLElement {
    const form = this.form!;
    const latest = this.latest!;
    const dirty = isDirty(form, latest);

    const enabledToggle = el("button", {
      className: "toggle",
      type: "button",
      role: "switch",
      "aria-checked": String(form.syncEnabled),
      "aria-label": "Scheduled sync enabled",
      onclick: () => {
        form.syncEnabled = !form.syncEnabled;
        this.touch();
      },
    });

    const label = form.syncEnabled
      ? el("span", { className: "toggle-label" }, ["Enabled"])
      : labelWithHelp("Disabled", HELP.syncDisabled);

    return el("div", { className: "panel panel--dominant" }, [
      el("p", { className: "panel__title" }, ["Scheduled sync"]),
      el("p", { className: "panel__description" }, [
        "When off, scheduled runs are paused. Manual runs are unaffected.",
      ]),
      el("div", { className: "row row--between row--gap-4" }, [
        el("div", { className: "row row--gap-4" }, [enabledToggle, label]),
        renderNextRunPreview(latest, form, dirty),
      ]),
    ]);
  }

  /* ---------------- 2. Schedule ---------------- */

  private renderSchedule(): HTMLElement {
    const form = this.form!;

    const kindSegmented = el(
      "div",
      { className: "segmented", role: "group", "aria-label": "Schedule kind" },
      (["daily", "weekly", "monthly"] as ScheduleKind[]).map((kind) =>
        el(
          "button",
          {
            className: "segmented__option",
            type: "button",
            "aria-pressed": String(form.scheduleKind === kind),
            onclick: () => {
              form.scheduleKind = kind;
              this.touch();
            },
          },
          [kindLabel(kind)]
        )
      )
    );

    const timeInput = el("input", {
      className: "input input--sm input--time",
      type: "time",
      value: form.scheduleTime,
      "aria-label": "Schedule time IST",
      onchange: (ev: Event) => {
        form.scheduleTime = (ev.target as HTMLInputElement).value;
        // Layout-stable change - update only the footer to preserve focus.
        this.touchLight();
      },
    });

    const conditional: HTMLElement[] = [];
    if (form.scheduleKind === "weekly") {
      conditional.push(
        field(
          "Weekdays",
          el(
            "div",
            { className: "segmented", role: "group", "aria-label": "Weekdays" },
            WEEKDAY_LABELS.map((label, idx) =>
              el(
                "button",
                {
                  className: "segmented__option",
                  type: "button",
                  "aria-pressed": String(form.weekdays.includes(idx)),
                  onclick: () => {
                    form.weekdays = form.weekdays.includes(idx)
                      ? form.weekdays.filter((d) => d !== idx)
                      : [...form.weekdays, idx].sort((a, b) => a - b);
                    this.touch();
                  },
                },
                [label]
              )
            )
          )
        )
      );
      if (form.weekdays.length === 0) {
        conditional.push(
          el("p", { className: "next-cron__empty" }, [
            "Select at least one weekday before saving a weekly schedule.",
          ])
        );
      }
    } else if (form.scheduleKind === "monthly") {
      conditional.push(
        field(
          "Day of month",
          numberInput(form.monthDay, 1, 28, "Month day", (v) => {
            form.monthDay = v;
            // Layout-stable change - update only the footer to preserve focus.
            this.touchLight();
          })
        )
      );
    }

    return el("div", { className: "panel panel--standard" }, [
      el("p", { className: "panel__title" }, ["Schedule"]),
      el("p", { className: "panel__description" }, [
        "Timezone is Asia/Kolkata (IST). Changes apply after save.",
      ]),
      el("div", { className: "stack stack--gap-6" }, [
        field(labelWithHelp("Frequency", HELP.scheduleType), kindSegmented),
        field("Time (IST, 24-hour)", timeInput),
        ...conditional,
      ]),
    ]);
  }

  /* ---------------- 4. Operational settings ---------------- */

  private renderOperational(): HTMLElement {
    const form = this.form!;
    return el("div", { className: "panel panel--standard" }, [
      el("p", { className: "panel__title" }, ["Operational settings"]),
      el("p", { className: "panel__description" }, [
        "How each run chooses timesheet date ranges and change lookbacks.",
      ]),
      el("div", { className: "panel-row-2up" }, [
        field(
          labelWithHelp("Sync window (1-60 days)", HELP.syncWindowDays),
          numberInput(form.timesheetSyncWindowDays, 1, 60, "Sync window days", (v) => {
            form.timesheetSyncWindowDays = v;
            this.touchLight();
          })
        ),
        field(
          labelWithHelp("Overlap (≥ 0 days)", HELP.overlapDays),
          numberInput(form.timesheetSyncOverlapDays, 0, undefined, "Overlap days", (v) => {
            form.timesheetSyncOverlapDays = v;
            this.touchLight();
          })
        ),
        field(
          labelWithHelp("Lookback (≥ 1 days)", HELP.lookbackDays),
          numberInput(form.timesheetSyncLookbackDays, 1, undefined, "Lookback days", (v) => {
            form.timesheetSyncLookbackDays = v;
            this.touchLight();
          })
        ),
        field(
          labelWithHelp("Last-modified overlap (≥ 0 hours)", HELP.lastModifiedOverlap),
          numberInput(
            form.lastModifiedOverlapHours,
            0,
            undefined,
            "Last-modified overlap hours",
            (v) => {
              form.lastModifiedOverlapHours = v;
              this.touchLight();
            }
          )
        ),
      ]),
    ]);
  }

  /* ---------------- Footer: dirty status + single primary Save ---------------- */

  private renderFooter(): HTMLElement {
    const form = this.form!;
    const latest = this.latest!;
    const dirty = isDirty(form, latest);

    const stateClass = dirty ? "status-line__state--dirty" : "status-line__state--clean";
    const stateLabel = dirty ? "UNSAVED" : "CLEAN";

    const actions: HTMLElement[] = [];
    if (dirty) {
      actions.push(
        el(
          "button",
          {
            className: "btn btn-ghost btn-sm",
            type: "button",
            disabled: this.saving,
            "aria-label": "Cancel unsaved changes",
            onclick: () => this.discard(),
          },
          ["Cancel"]
        )
      );
    }
    actions.push(
      el(
        "button",
        {
          className: "btn btn-primary btn-sm",
          type: "button",
          disabled: this.saving || !dirty,
          "aria-label": "Save configuration",
          onclick: () => void this.save(),
        },
        [this.saving ? "Saving…" : "Save changes"]
      )
    );

    return el("div", { className: "config-footer" }, [
      el("div", { className: "status-line" }, [
        el("span", { className: `status-line__state ${stateClass}` }, [`CONFIG STATUS: ${stateLabel}`]),
        el("span", { className: "status-line__meta" }, [
          latest.updatedAt
            ? el("span", { title: latest.updatedAt }, [
                `last modified ${relativeTime(latest.updatedAt)}`,
              ])
            : "last modified -",
        ]),
      ]),
      el("div", { className: "status-line__actions" }, actions),
    ]);
  }
}

/* ---------------- Read-only system values ---------------- */

function renderReadOnlySystem(s: SyncSettings): HTMLElement {
  return el("div", { className: "panel panel--utility" }, [
    el("p", { className: "panel__title" }, ["System values"]),
    el("p", { className: "panel__description" }, [
      "Read-only values from the server and environment.",
    ]),
    el("div", { className: "next-cron" }, [
      row("Timezone", el("span", { className: "mono" }, [s.timezone ?? EMPTY])),
      row(
        "Next run (IST)",
        s.nextFireAtIst ? renderAbsoluteTime(s.nextFireAtIst) : el("span", {}, [EMPTY])
      ),
      row(
        "Next run (UTC)",
        s.nextFireAtUtc
          ? renderAbsoluteTime(s.nextFireAtUtc, { timeZone: "UTC", zoneLabel: "UTC" })
          : el("span", {}, [EMPTY])
      ),
      row(
        "Last chain status",
        s.lastChainStatus ? renderStatusText(...toneOf(s.lastChainStatus)) : el("span", {}, [EMPTY])
      ),
      row("Last chain completed", renderAbsoluteTime(s.lastChainCompletedAt)),
      row("Settings last updated", renderAbsoluteTime(s.updatedAt)),
      row(
        "Default page size",
        el("span", { className: "tnum" }, [String(s.apiDefaultPageSize)])
      ),
      row("Max page size", el("span", { className: "tnum" }, [String(s.apiMaxPageSize)])),
    ]),
  ]);
}

function renderNextRunPreview(latest: SyncSettings, form: FormState, dirty: boolean): HTMLElement {
  if (!form.syncEnabled) {
    return el("div", { className: "callout" }, [
      el("span", { className: "callout__label" }, ["Next run"]),
      el("span", { className: "callout__value" }, ["Paused"]),
    ]);
  }

  if (dirty) {
    return el("div", { className: "callout" }, [
      el("span", { className: "callout__label" }, ["Next run"]),
      el("span", { className: "callout__value" }, ["Updates after save"]),
    ]);
  }

  if (latest.nextFireAtIst) {
    return el("div", { className: "callout", title: latest.nextFireAtUtc ?? latest.nextFireAtIst }, [
      el("span", { className: "callout__label" }, ["Next run"]),
      el("span", { className: "callout__value" }, [absoluteTime(latest.nextFireAtIst)]),
    ]);
  }

  return el("div", { className: "callout" }, [
    el("span", { className: "callout__label" }, ["Next run"]),
    el("span", { className: "callout__value" }, [EMPTY]),
  ]);
}

function saveSummary(form: FormState, latest: SyncSettings): string {
  const lines: string[] = [];
  const baseline = toFormState(latest);

  if (form.syncEnabled !== baseline.syncEnabled) {
    lines.push(form.syncEnabled ? "Enable scheduled sync." : "Disable scheduled sync.");
  }

  const scheduleChanged =
    form.scheduleKind !== baseline.scheduleKind ||
    form.scheduleTime !== baseline.scheduleTime ||
    form.monthDay !== baseline.monthDay ||
    !sameWeekdays(form.weekdays, baseline.weekdays);

  if (scheduleChanged) {
    let schedule = `${kindLabel(form.scheduleKind)} at ${form.scheduleTime} IST`;
    if (form.scheduleKind === "weekly") {
      schedule += ` on ${form.weekdays.map((d) => WEEKDAY_LABELS[d] ?? d).join(", ") || "(no days)"}`;
    } else if (form.scheduleKind === "monthly") {
      schedule += ` on day ${form.monthDay}`;
    }
    lines.push(`Set schedule to ${schedule}.`);
  }

  if (form.timesheetSyncLookbackDays !== baseline.timesheetSyncLookbackDays) {
    if (form.timesheetSyncLookbackDays > baseline.timesheetSyncLookbackDays) {
      lines.push(
        `Increase timesheet lookback to ${form.timesheetSyncLookbackDays} days. The next timesheet sync will re-fetch from today back that far.`
      );
    } else {
      lines.push(
        `Decrease timesheet lookback to ${form.timesheetSyncLookbackDays} days. Timesheet rows older than that window will be deleted immediately.`
      );
    }
  }

  if (
    form.timesheetSyncWindowDays !== baseline.timesheetSyncWindowDays ||
    form.timesheetSyncOverlapDays !== baseline.timesheetSyncOverlapDays ||
    form.lastModifiedOverlapHours !== baseline.lastModifiedOverlapHours
  ) {
    lines.push("Update operational settings.");
  }

  lines.push("The server will reload the schedule from the saved settings.");
  return lines.join(" ");
}

function numberInput(
  value: number,
  min: number,
  max: number | undefined,
  ariaLabel: string,
  onChange: (v: number) => void
): HTMLElement {
  return el("input", {
    className: "input input--sm",
    type: "number",
    min: String(min),
    max: max !== undefined ? String(max) : undefined,
    value: String(value),
    "aria-label": ariaLabel,
    onchange: (ev: Event) => {
      const parsed = Number((ev.target as HTMLInputElement).value);
      if (!Number.isNaN(parsed)) onChange(parsed);
    },
  });
}

function field(label: ElChild, control: HTMLElement): HTMLElement {
  return el("div", { className: "field" }, [el("span", { className: "field__label" }, [label]), control]);
}

function row(label: ElChild, value: HTMLElement): HTMLElement {
  return el("div", { className: "next-cron__row" }, [
    el("span", { className: "next-cron__label" }, [label]),
    value,
  ]);
}

function kindLabel(kind: ScheduleKind): string {
  switch (kind) {
    case "daily":
      return "Daily";
    case "weekly":
      return "Weekly";
    case "monthly":
      return "Monthly";
    default:
      return kind;
  }
}

function toneOf(status: string): [string, "success" | "danger" | "accent" | "muted"] {
  const t = runStatusTone(status);
  return [t.label, t.tone];
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const parts = [err.message, ...(err.errors ?? [])].filter(Boolean);
    return parts.join(" - ") || "Request failed.";
  }
  return "Something went wrong loading this data.";
}
